import { inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  catchError,
  concat,
  EMPTY,
  finalize,
  firstValueFrom,
  NEVER,
  Observable,
  switchMap,
  timeout,
} from 'rxjs';
import { OpenAPIApiService } from '@data-access';
import { CABINET_TYPE } from '../constants/platform-type.constants';
import { environment } from '@shared-app-config';

/** Guards against a reload loop when the cache cannot be refreshed. */
const RELOAD_THROTTLE_MS = 15_000;
const LAST_RELOAD_KEY = 'app-version-reload-at';
/** A reload never waits on an API ping longer than this. */
const API_PING_TIMEOUT_MS = 5_000;
const REMEMBERED_KEY = 'app-version-remembered';
/** Older than this, a remembered value belongs to a reload that never brought up its reader. */
const REMEMBERED_TTL_MS = 60_000;
/** The version a build carries until CI stamps one — a local build skips the check. */
const UNSTAMPED_VERSION = '0';

@Injectable({ providedIn: 'root' })
export class AppVersionService {
  private readonly openApiService = inject(OpenAPIApiService);
  private readonly cabinetType = inject(CABINET_TYPE);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly reloading = signal(false);

  /** True once a reload has started — the page is about to unload. */
  readonly isReloading = this.reloading.asReadonly();

  checkVersionOnStartup(): Observable<unknown> {
    // A prerender runs at build time: there is no deployed version to compare, nor a page to reload.
    if (!this.isBrowser) {
      return EMPTY;
    }

    return this.openApiService.getFrontVersion(this.cabinetType).pipe(
      switchMap((version) => {
        const envValue =
          environment.version?.[this.cabinetType as keyof typeof environment.version];

        if (envValue === UNSTAMPED_VERSION) {
          return EMPTY;
        }

        if (version?.value === envValue) {
          return EMPTY;
        }

        if (this.isReloadThrottled()) {
          return EMPTY;
        }

        // Holds the bootstrap while the page unloads: guards started now would only see their
        // requests aborted by the reload and report them as errors.
        return concat(this.resetCacheAndReload$(), NEVER);
      }),
      catchError(() => EMPTY),
    );
  }

  /** Reloads the page, or loads `url` as a fresh page when given (a navigation that failed). */
  async resetCacheAndReload(url?: string): Promise<void> {
    try {
      await firstValueFrom(this.resetCacheAndReload$(url));
    } catch {
      // resetCacheAndReload$() completes without emitting (EMPTY) when throttled — nothing to do.
    }
  }

  /** A request cut off by the page reload (status 0): noise, not an unreachable backend. */
  isAbortedByReload(error: unknown): boolean {
    return this.reloading() && error instanceof HttpErrorResponse && error.status === 0;
  }

  /** True while a reload would be skipped because the app reloaded less than 15 s ago. */
  isReloadThrottled(): boolean {
    const lastReload = Number(sessionStorage.getItem(LAST_RELOAD_KEY)) || 0;

    return Date.now() - lastReload < RELOAD_THROTTLE_MS;
  }

  /** Keeps `value` for the page the coming reload brings up, which reads it with `takeRemembered`. */
  rememberForReload(key: string, value: string): void {
    try {
      sessionStorage.setItem(REMEMBERED_KEY, JSON.stringify({ key, value, at: Date.now() }));
    } catch {
      // Blocked or full storage only loses the reopen; the reload itself must still happen.
    }
  }

  /** The value remembered under `key` before the reload; read once. */
  takeRemembered(key: string): string | null {
    if (!this.isBrowser) {
      return null;
    }

    let remembered: { key?: unknown; value?: unknown; at?: unknown };
    try {
      remembered = JSON.parse(sessionStorage.getItem(REMEMBERED_KEY) ?? 'null') ?? {};
    } catch {
      remembered = {};
    }

    if (remembered.key !== key) {
      return null;
    }

    sessionStorage.removeItem(REMEMBERED_KEY);
    const age = Date.now() - Number(remembered.at);
    const isFresh = age >= 0 && age < REMEMBERED_TTL_MS;

    return isFresh && typeof remembered.value === 'string' ? remembered.value : null;
  }

  private resetCacheAndReload$(url?: string): Observable<void> {
    if (this.isReloadThrottled()) {
      return EMPTY;
    }

    sessionStorage.setItem(LAST_RELOAD_KEY, String(Date.now()));
    this.reloading.set(true);

    return this.openApiService.getApiStatus().pipe(
      timeout(API_PING_TIMEOUT_MS),
      catchError(() => EMPTY),
      finalize(() => (url ? window.location.assign(url) : window.location.reload())),
    );
  }
}
